<script setup>
import CreateGroupDrawer from '@/views/groups/CreateGroupDrawer.vue'
import axios from '@/helpers/axios'

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const groups = ref([])
const selfUser = ref(useCookie('userData').value)
const showCreateGroupDrawer = ref(false)

const getGroups = () => {
  axios.get('/groups').then(r => {
    groups.value = r.data.data
  }).catch(e => {
    console.error(e)
  })
}

getGroups()
</script>

<template>
  <div>

    <div class="page-header mb-4">
      <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between gap-4">
        <div>
          <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
            {{ $t('groups.title') }}
          </h2>
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ $t('groups.description') }}
          </p>
        </div>

        <VBtn
          color="primary"
          class="text-none px-5 py-3 font-weight-medium"
          size="large"
          rounded="lg"
          variant="tonal"
          @click="showCreateGroupDrawer = true"
        >
          <VIcon start icon="tabler-plus" size="20" />
          {{ $t('groups.create_group') }}
        </VBtn>
      </div>
    </div>

    <VRow v-if="groups.length" class="match-height">
      <VCol
        v-for="group in groups"
        :key="group.uuid"
        cols="12"
        md="6"
        lg="4"
      >
        <VCard
          class="overflow-visible pa-4 group-card"
          elevation="2"
          style="border-radius: 16px;"
        >
          <!-- Header -->
          <div class="d-flex justify-space-between align-start mb-4 flex-wrap gap-3">
            <div class="d-flex align-center gap-3">
              <VAvatar
                color="primary"
                variant="tonal"
                size="44"
                class="text-h6 font-weight-semibold"
              >
                {{ group.name?.charAt(0)?.toUpperCase() }}
              </VAvatar>
              <div>
                <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
                  {{ group.name }}
                </h4>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ formatDate(group.created_at) }}
                </p>
              </div>
            </div>

            <div class="d-flex align-center gap-2">
              <VChip
                :color="group.admin_uuid === selfUser?.uuid ? 'success' : 'grey'
                "
                size="small"
                variant="tonal"
                class="font-weight-medium"
              >
                {{ group.admin_uuid === selfUser?.uuid ? $t('groups.admin') : $t('groups.member') }}
              </VChip>

              <VBtn
                icon
                size="small"
                variant="tonal"
                color="primary"
                :to="{ name: 'groups-uuid', params: { uuid: group.uuid } }"
              >
                <VIcon icon="tabler-arrow-right" size="x-large" />
                <VTooltip activator="parent" location="top">
                  {{ $t('layout.open') }}
                </VTooltip>
              </VBtn>
            </div>
          </div>

          <!-- Middle section -->
          <div class="mb-4">
            <div class="d-flex align-center mb-1">
              <VIcon icon="tabler-devices" size="18" class="me-2 text-primary" />
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('groups.devices_number', { number: group.devices_count }) }}
              </span>
            </div>
            <div class="d-flex align-center mb-1">
              <VIcon icon="tabler-camera" size="18" class="me-2 text-primary" />
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('groups.webcams_number', { number: group.webcams.length }) }}
              </span>
            </div>

          </div>

          <VDivider class="my-4" />

          <div class="d-flex justify-center  flex-wrap gap-3">
            <div class="d-flex justify-center">
              <template v-if="group.users?.length > 0">
                <VAvatar
                  v-for="user in (group.users?.slice(0, 4) || [])"
                  :key="user.uuid"
                  size="34"
                  class="ms-n2 border border-white elevation-1"
                >
                  <VImg :src="user.avatar_thumbnail" cover />
                  <VTooltip activator="parent" location="top">
                    {{ user.name }}
                  </VTooltip>
                </VAvatar>

                <div
                  v-if="group.users?.length > 4"
                  class="ms-n2 bg-grey-lighten-3 text-caption text-medium-emphasis d-flex align-center justify-center rounded-circle more-users-chip"
                >
                  +{{ group.users?.length - 4 }}
                </div>
              </template>
              <span v-else class="text-body-2 text-medium-emphasis">
                {{ $t('groups.no_users') }}
              </span>
            </div>
          </div>

        </VCard>
      </VCol>
    </VRow>
    <div v-else>
      <div
        class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
      >
        <VIcon icon="tabler-user-off" size="48" color="grey" class="mb-3" />
        <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
          {{ $t('groups.no_groups') }}
        </h4>
      </div>
    </div>
    <CreateGroupDrawer
      :is-drawer-open="showCreateGroupDrawer"
      @update:is-drawer-open="(val) => showCreateGroupDrawer = val"
      @submit="getGroups"
    />
  </div>
</template>

<style scoped>
.page-header {
  background: linear-gradient(180deg, var(--v-theme-surface), transparent);
  border-radius: 16px;
  padding: 1.5rem 0rem;
  transition: all 0.25s ease;
}

@media (max-width: 600px) {
  .page-header {
    padding: 1.25rem 1rem;
  }
  .page-header h2 {
    font-size: 1.4rem;
  }
}

.group-card {
  transition: all 0.25s ease;
}

.group-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  border-color: var(--v-theme-primary);
}
</style>
